import { Controller } from "@hotwired/stimulus";

// Gmail-style recipient field: pick a suggestion or type an address, either way you get a removable
// tile. Generalizes admin/massimo/share_recipient_controller.js from one User to N arbitrary addresses.
//
// Recipients live in this.recipients and are mirrored into hidden `name[]` inputs on every change, so a
// suggested address and a typed one are indistinguishable by the time the form posts.
export default class extends Controller {
  static targets = [
    "search",
    "dropdown",
    "item",
    "empty",
    "tiles",
    "tileTemplate",
    "hiddenContainer",
    "initial",
    "field",
  ];

  static values = { name: String };

  // Deliberately permissive -- the server validates with EmailValidator. This only decides whether a
  // typed value is plausible enough to become a tile rather than being silently dropped.
  static EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

  connect() {
    this.recipients = JSON.parse(this.initialTarget.textContent || "[]");
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.handleClickOutside);
    this.render();
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  focusSearch(event) {
    // Clicking a tile's remove button shouldn't also reopen the dropdown.
    if (event.target.closest("[data-tile]")) return;

    this.searchTarget.focus();
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden");
    this.filter();
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) this.hideDropdown();
  }

  filter() {
    const term = this.searchTarget.value.trim().toLowerCase();
    let visible = 0;

    this.itemTargets.forEach((item) => {
      const taken = this.#has(item.dataset.value);
      const matches = item.textContent.toLowerCase().includes(term);
      const show = matches && !taken;
      item.classList.toggle("hidden", !show);
      if (show) visible += 1;
    });

    // The hint doubles as the "no matches" state: whatever you typed can still be added directly.
    this.emptyTarget.classList.toggle("hidden", visible > 0 || term.length === 0);

    // Only ever close from here. Opening is driven by focus/typing, so re-rendering tiles can't pop the
    // dropdown back open over the form's buttons and swallow the next click.
    if (visible === 0 && term.length === 0) this.hideDropdown();
  }

  onKeydown(event) {
    if (["Enter", ",", ";", "Tab"].includes(event.key)) {
      // Tab with an empty box should move focus on rather than being swallowed.
      if (event.key === "Tab" && this.searchTarget.value.trim() === "") return;

      event.preventDefault();
      this.#commitTyped();
      return;
    }

    if (event.key === "Backspace" && this.searchTarget.value === "" && this.recipients.length > 0) {
      event.preventDefault();
      this.recipients.pop();
      this.#renderChanged();
    }

    if (event.key === "Escape") this.hideDropdown();
  }

  // Commit on blur too, so clicking Save with a half-typed address doesn't quietly discard it. Blur also
  // means the user is done here -- leaving the dropdown open would cover whatever they clicked next.
  onBlur() {
    this.#commitTyped();
    this.hideDropdown();
  }

  selectItem(event) {
    event.preventDefault();
    this.#add(event.currentTarget.dataset.value, event.currentTarget.querySelector(".font-medium")?.textContent);
    this.searchTarget.value = "";
    this.searchTarget.focus();
  }

  removeTile(event) {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.closest("[data-tile]").dataset.value;
    this.recipients = this.recipients.filter((r) => r.value !== value);
    this.#renderChanged();
  }

  // The hidden inputs are rebuilt programmatically, which fires no events -- so anything watching the
  // form for edits (the unsaved-changes guard) would never see a recipient added or removed. Dispatched
  // from the mutation paths only, never from connect's initial render.
  #renderChanged() {
    this.render();
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  render() {
    this.tilesTarget.replaceChildren();
    this.hiddenContainerTarget.replaceChildren();

    this.recipients.forEach((recipient) => {
      const tile = this.tileTemplateTarget.content.cloneNode(true);
      const root = tile.querySelector("[data-tile]");
      root.dataset.value = recipient.value;
      // textContent, never innerHTML: a label can carry a person's name from the database.
      tile.querySelector("[data-label]").textContent = recipient.label
        ? `${recipient.label} (${recipient.value})`
        : recipient.value;

      if (!this.constructor.EMAIL.test(recipient.value)) {
        root.classList.remove("bg-secondary", "text-secondary-foreground");
        root.classList.add("bg-destructive/10", "text-destructive");
        root.title = "This doesn't look like a valid email address.";
      }

      this.tilesTarget.appendChild(tile);

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `${this.nameValue}[]`;
      input.value = recipient.value;
      this.hiddenContainerTarget.appendChild(input);
    });

    this.filter();
    // Once the field is scrolling, keep the input (and so the newest tile) in view -- otherwise adding a
    // recipient looks like it did nothing.
    this.fieldTarget.scrollTop = this.fieldTarget.scrollHeight;
  }

  #commitTyped() {
    const raw = this.searchTarget.value.trim().replace(/[,;]$/, "").trim();
    if (raw === "") return;

    this.#add(raw, this.#labelFor(raw));
    this.searchTarget.value = "";
  }

  #add(value, label = null) {
    const normalized = value.trim().toLowerCase();
    if (normalized === "" || this.#has(normalized)) return;

    this.recipients = [...this.recipients, { value: normalized, label: label || null }];
    this.#renderChanged();
  }

  #labelFor(value) {
    const match = this.itemTargets.find(
      (item) => item.dataset.value.toLowerCase() === value.toLowerCase()
    );
    return match?.querySelector(".font-medium")?.textContent || null;
  }

  #has(value) {
    return this.recipients.some((r) => r.value.toLowerCase() === value.toLowerCase());
  }
}
